import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Sparkles, Trash2, Save } from 'lucide-react';
import {
  legacyFachMappingApi,
  type LegacyFachMappingsResponse,
  type FachOption,
  type LegacyFachMappingDto,
} from '@/lib/api';
import { LEGACY_FACH_MASTER } from '@/lib/migrationCaseWizard';

interface RowState {
  legacyId: number;
  legacyName: string;
  // Aktuell ausgewählter neuer Fach (lokal — kann sich vom gespeicherten Wert unterscheiden)
  selectedNewFachId: number | null;
  // Persistiert: ID aus DB, sonst null
  persistedNewFachId: number | null;
  // Markiert Zeilen mit unsaved changes
  dirty: boolean;
}

const buildRows = (data: LegacyFachMappingsResponse | null): RowState[] => {
  const legacyEntries = Object.entries(LEGACY_FACH_MASTER)
    .map(([k, v]) => ({ legacyId: Number(k), legacyName: v }))
    .filter((r) => Number.isFinite(r.legacyId) && r.legacyId > 0)
    .sort((a, b) => a.legacyId - b.legacyId);

  const byLegacy = new Map<number, LegacyFachMappingDto>();
  if (data?.mappings) {
    for (const m of data.mappings) byLegacy.set(m.legacy_id, m);
  }

  return legacyEntries.map((entry) => {
    const existing = byLegacy.get(entry.legacyId);
    return {
      legacyId: entry.legacyId,
      legacyName: entry.legacyName,
      selectedNewFachId: existing ? existing.new_fach_id : null,
      persistedNewFachId: existing ? existing.new_fach_id : null,
      dirty: false,
    };
  });
};

const findBestNameMatch = (legacyName: string, options: FachOption[]): FachOption | null => {
  const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');
  const target = norm(legacyName);
  let hit = options.find((o) => norm(o.name) === target);
  if (hit) return hit;
  hit = options.find((o) => norm(o.name).startsWith(target) || target.startsWith(norm(o.name)));
  if (hit) return hit;
  hit = options.find((o) => norm(o.name).includes(target) || target.includes(norm(o.name)));
  return hit ?? null;
};

const LegacyFachMappingsPage: React.FC = () => {
  const [data, setData] = useState<LegacyFachMappingsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<RowState[]>([]);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await legacyFachMappingApi.list();
      setData(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setRows(buildRows(data));
  }, [data]);

  const fachOptions = data?.faecher ?? [];
  const activeFachOptions = useMemo(() => fachOptions.filter((f) => f.aktiv), [fachOptions]);
  const fachById = useMemo(() => {
    const map = new Map<number, FachOption>();
    fachOptions.forEach((f) => map.set(f.id, f));
    return map;
  }, [fachOptions]);

  const dirtyCount = rows.filter((r) => r.dirty).length;
  const unmappedCount = rows.filter((r) => r.selectedNewFachId === null).length;

  const handleSelectChange = (legacyId: number, valueRaw: string) => {
    const value = valueRaw === '__unset__' ? null : Number(valueRaw);
    setRows((prev) => prev.map((r) => {
      if (r.legacyId !== legacyId) return r;
      const next = value !== null && Number.isFinite(value) ? value : null;
      return {
        ...r,
        selectedNewFachId: next,
        dirty: next !== r.persistedNewFachId,
      };
    }));
  };

  const handleSaveRow = async (row: RowState) => {
    if (row.selectedNewFachId === null) {
      if (row.persistedNewFachId === null) return;
      setSavingId(row.legacyId);
      try {
        await legacyFachMappingApi.delete(row.legacyId);
        await load();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Löschen fehlgeschlagen');
      } finally {
        setSavingId(null);
      }
      return;
    }
    setSavingId(row.legacyId);
    try {
      await legacyFachMappingApi.upsert({
        legacy_fach_id: row.legacyId,
        new_fach_id: row.selectedNewFachId,
      });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setSavingId(null);
    }
  };

  const handleAutoSuggest = () => {
    setNotice(null);
    setRows((prev) => {
      let filled = 0;
      const next = prev.map((r) => {
        if (r.selectedNewFachId !== null) return r;
        const match = findBestNameMatch(r.legacyName, activeFachOptions);
        if (!match) return r;
        filled += 1;
        return {
          ...r,
          selectedNewFachId: match.id,
          dirty: match.id !== r.persistedNewFachId,
        };
      });
      setNotice(`${filled} Vorschläge eingefügt. Bitte prüfen und „Alle Änderungen speichern".`);
      return next;
    });
  };

  const handleBulkSave = async () => {
    const toWrite = rows.filter((r) => r.dirty && r.selectedNewFachId !== null);
    if (toWrite.length === 0) return;
    setBulkSaving(true);
    setError(null);
    try {
      const res = await legacyFachMappingApi.bulk({
        mappings: toWrite.map((r) => ({
          legacy_fach_id: r.legacyId,
          new_fach_id: r.selectedNewFachId as number,
        })),
      });
      await load();
      setNotice(`${res.written} Mapping(s) gespeichert${res.skipped > 0 ? `, ${res.skipped} übersprungen` : ''}.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Bulk-Speichern fehlgeschlagen');
    } finally {
      setBulkSaving(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Legacy-Fach-Mappings</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Ordnet die Legacy-Fach-IDs aus dem Alt-System der neuen System-Fach-ID zu.
            Mappings sind global und werden sowohl bei der Lehrer-Migration als auch bei
            der Auftrags-/Vertragsanpassungs-Migration verwendet.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Mappings</span>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={handleAutoSuggest}
                disabled={loading || activeFachOptions.length === 0}
              >
                <Sparkles className="mr-2 h-4 w-4" /> Auto-Vorschläge generieren
              </Button>
              <Button
                type="button"
                onClick={handleBulkSave}
                disabled={dirtyCount === 0 || bulkSaving}
              >
                {bulkSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Alle Änderungen speichern{dirtyCount > 0 ? ` (${dirtyCount})` : ''}
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-xs text-muted-foreground">
            {rows.length} Legacy-Fächer · {unmappedCount} unmapped · {dirtyCount} geänderte Zeilen
          </div>

          {error ? (
            <div className="rounded-md border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
              {error}
            </div>
          ) : null}
          {notice ? (
            <div className="rounded-md border border-primary/40 bg-primary/10 p-3 text-sm text-primary-foreground">
              {notice}
            </div>
          ) : null}

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Lade Mappings…
            </div>
          ) : (
            <div className="overflow-hidden rounded-md border border-border/70">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left">Legacy-ID</th>
                    <th className="px-3 py-2 text-left">Legacy-Name (Alt-System)</th>
                    <th className="px-3 py-2 text-left">Neues Fach</th>
                    <th className="px-3 py-2 text-right">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const persistedName = row.persistedNewFachId
                      ? fachById.get(row.persistedNewFachId)?.name
                      : null;
                    return (
                      <tr
                        key={row.legacyId}
                        className={`border-t border-border/40 ${row.dirty ? 'bg-amber-500/5' : ''}`}
                      >
                        <td className="px-3 py-2 font-mono text-xs">{row.legacyId}</td>
                        <td className="px-3 py-2">{row.legacyName}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <Select
                              value={row.selectedNewFachId !== null ? String(row.selectedNewFachId) : '__unset__'}
                              onValueChange={(v) => handleSelectChange(row.legacyId, v)}
                            >
                              <SelectTrigger className="min-w-[260px]">
                                <SelectValue placeholder="— wählen —" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__unset__">— kein Mapping —</SelectItem>
                                {fachOptions.map((f) => (
                                  <SelectItem key={f.id} value={String(f.id)}>
                                    {f.name}{!f.aktiv ? ' (inaktiv)' : ''}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {row.dirty && row.persistedNewFachId !== null ? (
                              <span className="text-xs text-muted-foreground">
                                vorher: {persistedName ?? `#${row.persistedNewFachId}`}
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant={row.dirty ? 'default' : 'outline'}
                              disabled={!row.dirty || savingId === row.legacyId}
                              onClick={() => handleSaveRow(row)}
                            >
                              {savingId === row.legacyId ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Save className="h-3.5 w-3.5" />
                              )}
                            </Button>
                            {row.persistedNewFachId !== null ? (
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                disabled={savingId === row.legacyId}
                                onClick={async () => {
                                  if (!confirm(`Mapping für „${row.legacyName}" wirklich löschen?`)) return;
                                  setSavingId(row.legacyId);
                                  try {
                                    await legacyFachMappingApi.delete(row.legacyId);
                                    await load();
                                  } catch (e) {
                                    setError(e instanceof Error ? e.message : 'Löschen fehlgeschlagen');
                                  } finally {
                                    setSavingId(null);
                                  }
                                }}
                              >
                                <Trash2 className="h-3.5 w-3.5 text-red-500" />
                              </Button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LegacyFachMappingsPage;
